/**
 * Scrollable main-image slider for the product media gallery.
 * Re-initializes whenever the element is connected so variant updates
 * that replace <media-gallery> keep swipe/nav/dots working.
 */
class ProductMediaSlider extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #startPos = 0;

  /** @type {number} */
  #currentTranslate = 0;

  /** @type {number} */
  #prevTranslate = 0;

  /** @type {number} */
  #animationID = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const track = this.querySelector('.product-media-slider__track');
    const slides = this.querySelectorAll('.product-media-slider__slide');
    const dots = this.querySelectorAll('.product-media-slider__dot');
    const prevBtn = this.querySelector('.product-media-slider__nav--prev');
    const nextBtn = this.querySelector('.product-media-slider__nav--next');

    if (!(track instanceof HTMLElement) || slides.length === 0) return;

    const { signal } = this.#controller;

    this.#currentIndex = 0;
    this.#isDragging = false;
    this.#currentTranslate = 0;
    this.#prevTranslate = 0;

    const goToSlide = (index) => {
      this.#currentIndex = (index + slides.length) % slides.length;
      this.#currentTranslate = this.#currentIndex * -this.offsetWidth;
      this.#prevTranslate = this.#currentTranslate;
      track.style.transform = `translateX(${this.#currentTranslate}px)`;
      updateDots();
    };

    const updateDots = () => {
      const totalSlides = slides.length;

      if (totalSlides <= 3) {
        for (const [index, dot] of dots.entries()) {
          if (!(dot instanceof HTMLElement)) continue;
          dot.classList.toggle('is-active', index === this.#currentIndex);
          dot.style.display = 'block';
        }
        return;
      }

      for (const [index, dot] of dots.entries()) {
        if (!(dot instanceof HTMLElement)) continue;
        dot.classList.remove('is-active');

        if (this.#currentIndex === 0) {
          dot.style.display = index <= 2 ? 'block' : 'none';
        } else if (this.#currentIndex === totalSlides - 1) {
          dot.style.display = index >= totalSlides - 3 ? 'block' : 'none';
        } else {
          const inWindow = index >= this.#currentIndex - 1 && index <= this.#currentIndex + 1;
          dot.style.display = inWindow ? 'block' : 'none';
        }

        if (index === this.#currentIndex) {
          dot.classList.add('is-active');
        }
      }
    };

    const getPositionX = (event) => {
      if (event instanceof MouseEvent) return event.pageX;
      if (event instanceof TouchEvent && event.touches[0]) return event.touches[0].clientX;
      return 0;
    };

    const animation = () => {
      track.style.transform = `translateX(${this.#currentTranslate}px)`;
      if (this.#isDragging) this.#animationID = requestAnimationFrame(animation);
    };

    const touchStart = (event) => {
      this.#isDragging = true;
      this.#startPos = getPositionX(event);
      this.#animationID = requestAnimationFrame(animation);
      track.style.transition = 'none';
      this.style.cursor = 'grabbing';
    };

    const touchMove = (event) => {
      if (!this.#isDragging) return;
      const currentPosition = getPositionX(event);
      this.#currentTranslate = this.#prevTranslate + currentPosition - this.#startPos;
    };

    const touchEnd = () => {
      if (!this.#isDragging) return;

      this.#isDragging = false;
      cancelAnimationFrame(this.#animationID);
      track.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      this.style.cursor = 'grab';

      const movedBy = this.#currentTranslate - this.#prevTranslate;
      if (movedBy < -50 && this.#currentIndex < slides.length - 1) {
        this.#currentIndex += 1;
      }
      if (movedBy > 50 && this.#currentIndex > 0) {
        this.#currentIndex -= 1;
      }

      goToSlide(this.#currentIndex);
    };

    updateDots();

    this.addEventListener('mousedown', touchStart, { signal });
    this.addEventListener('mousemove', touchMove, { signal });
    this.addEventListener('mouseup', touchEnd, { signal });
    this.addEventListener('mouseleave', touchEnd, { signal });

    this.addEventListener('touchstart', touchStart, { passive: true, signal });
    this.addEventListener('touchmove', touchMove, { passive: true, signal });
    this.addEventListener('touchend', touchEnd, { signal });

    this.addEventListener('contextmenu', (event) => event.preventDefault(), { signal });

    for (const slide of slides) {
      slide.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    }

    prevBtn?.addEventListener('click', () => goToSlide(this.#currentIndex - 1), { signal });
    nextBtn?.addEventListener('click', () => goToSlide(this.#currentIndex + 1), { signal });

    for (const [index, dot] of dots.entries()) {
      dot.addEventListener('click', () => goToSlide(index), { signal });
    }

    this.style.cursor = 'grab';
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    cancelAnimationFrame(this.#animationID);
  }
}

if (!customElements.get('product-media-slider')) {
  customElements.define('product-media-slider', ProductMediaSlider);
}
